
import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from '../i18n/LanguageContext'
import { supabase } from '../supabase'

const GEMINI_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent'
const MAX_CHARS  = 500

// Mobile bottom nav (.pc-mobile-nav) is ~64px tall + safe-area inset.
// Tara's FAB must float above it, not on top of it.
const MOBILE_BREAKPOINT = 767
const MOBILE_MIN_BOTTOM = 76
const DESKTOP_MIN_BOTTOM = 8
const getMinBottom = () => (typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT) ? MOBILE_MIN_BOTTOM : DESKTOP_MIN_BOTTOM
const getDefaultBottom = () => (typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT) ? MOBILE_MIN_BOTTOM : 24

const PLAN_LIMITS = {
  trial:    0,
  starter:  0,
  growth:   50,
  pro:      200,
  business: 500,
}

const SUGGESTIONS = {
  en: [
    'How are my sales this month?',
    'Which products are low on stock?',
    'How many pending orders do I have?',
    'Who are my top customers?',
  ],
  bn: [
    'এই মাসে সেল কেমন হচ্ছে?',
    'কোন প্রোডাক্টের স্টক কম?',
    'কতটি pending order আছে?',
    'সেরা কাস্টমার কে?',
  ]
}

const GREETINGS = {
  en: `Hi! I'm Tara ✦\nYour AI Business Agent at PageConnect. Ask me anything — your sales, stock, customers, or what's trending this season.`,
  bn: `হ্যালো! আমি Tara ✦\nতোমার PageConnect AI Business Agent। যেকোনো কিছু জিজ্ঞেস করো — সেল, স্টক, কাস্টমার বা বিজনেসের যেকোনো বিষয়ে।`,
}

function TaraStarIcon({ size = 18, color = '#2DD4B0' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22C12 22 11.2 16.5 9.5 14.5C7.8 12.5 2 12 2 12C2 12 7.8 11.5 9.5 9.5C11.2 7.5 12 2 12 2Z" fill={color}/>
      <path d="M19 3C19 3 19.4 5.2 20.2 6C21 6.8 23 7 23 7C23 7 21 7.2 20.2 8C19.4 8.8 19 11 19 11C19 11 18.6 8.8 17.8 8C17 7.2 15 7 15 7C15 7 17 6.8 17.8 6C18.6 5.2 19 3 19 3Z" fill={color} opacity="0.7"/>
      <path d="M5 16C5 16 5.25 17.3 5.75 17.75C6.25 18.25 7.5 18.5 7.5 18.5C7.5 18.5 6.25 18.75 5.75 19.25C5.25 19.75 5 21 5 21C5 21 4.75 19.75 4.25 19.25C3.75 18.75 2.5 18.5 2.5 18.5C2.5 18.5 3.75 18.25 4.25 17.75C4.75 17.3 5 16 5 16Z" fill={color} opacity="0.5"/>
    </svg>
  )
}

function TypingIndicator() {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'5px', padding:'12px 16px', background:'var(--bg-card)', borderRadius:'18px 18px 18px 4px', border:'1px solid var(--border)', width:'fit-content', boxShadow:'var(--shadow-sm)' }}>
      {[0,1,2].map(i=>(
        <div key={i} style={{ width:'7px', height:'7px', borderRadius:'50%', background:'#2DD4B0', animation:`taraTyping 1.2s ease-in-out ${i*0.2}s infinite` }}/>
      ))}
    </div>
  )
}

export default function Tara() {
  const { seller, getSubscriptionPlan } = useAuth()
  const { lang } = useTranslation()

  const plan      = getSubscriptionPlan?.() || 'trial'
  const limit     = PLAN_LIMITS[plan] || 0
  const [open, setOpen]               = useState(false)
  const [messages, setMessages]       = useState([])
  const [input, setInput]             = useState('')
  const [loading, setLoading]         = useState(false)
  const [queriesUsed, setQueriesUsed] = useState(0)
  const [initialized, setInitialized] = useState(false)

  // Drag — separate from click
  const fabRef       = useRef()
  const panelRef     = useRef()
  const dragRef      = useRef({ dragging: false, moved: false, startX: 0, startY: 0, origRight: 24, origBottom: 24 })
  const [pos, setPos] = useState(() => ({ right: 24, bottom: getDefaultBottom() }))

  const messagesEndRef = useRef()
  const inputRef       = useRef()

  const remaining = Math.max(0, limit - queriesUsed)
  const canAsk    = limit > 0 && remaining > 0
  const charsLeft = MAX_CHARS - input.length

  useEffect(() => {
    if (seller?.id) fetchQueryCount()
  }, [seller?.id]) // eslint-disable-line

  useEffect(() => {
    if (open && !initialized && seller?.id) {
      setMessages([{ role:'assistant', text:GREETINGS[lang]||GREETINGS.en }])
      setInitialized(true)
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 150)
  }, [open, initialized, seller?.id, lang]) // eslint-disable-line

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior:'smooth' })
  }, [messages, loading])

  // Close panel on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          fabRef.current && !fabRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Re-clamp position if viewport crosses the mobile breakpoint (e.g. rotation, resize)
  // so Tara never ends up hidden behind the mobile bottom nav.
  useEffect(() => {
    const handler = () => {
      const min = getMinBottom()
      setPos(p => p.bottom < min ? { ...p, bottom: min } : p)
    }
    window.addEventListener('resize', handler)
    window.addEventListener('orientationchange', handler)
    return () => { window.removeEventListener('resize', handler); window.removeEventListener('orientationchange', handler) }
  }, [])

  // ── Drag logic — track movement to distinguish click vs drag ──
  const onMouseDown = useCallback((e) => {
    // Only drag on the wrapper div, not the button itself for click
    dragRef.current = {
      dragging: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      origRight: pos.right,
      origBottom: pos.bottom,
      screenW: window.innerWidth,
      screenH: window.innerHeight,
    }

    const onMove = (ev) => {
      const dx = Math.abs(ev.clientX - dragRef.current.startX)
      const dy = Math.abs(ev.clientY - dragRef.current.startY)
      if (dx > 4 || dy > 4) {
        dragRef.current.moved = true
        const rect = fabRef.current?.getBoundingClientRect()
        const rw = rect?.width || 56
        const rh = rect?.height || 56
        const newRight  = Math.max(8, Math.min(dragRef.current.screenW - rw - 8, dragRef.current.origRight - (ev.clientX - dragRef.current.startX)))
        const newBottom = Math.max(getMinBottom(), Math.min(dragRef.current.screenH - rh - 8, dragRef.current.origBottom - (ev.clientY - dragRef.current.startY)))
        setPos({ right: newRight, bottom: newBottom })
      }
    }

    const onUp = () => {
      dragRef.current.dragging = false
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [pos])

  const onTouchStart = useCallback((e) => {
    const t = e.touches[0]
    dragRef.current = {
      dragging: true, moved: false,
      startX: t.clientX, startY: t.clientY,
      origRight: pos.right, origBottom: pos.bottom,
      screenW: window.innerWidth, screenH: window.innerHeight,
    }

    const onMove = (ev) => {
      ev.preventDefault()
      const tt = ev.touches[0]
      const dx = Math.abs(tt.clientX - dragRef.current.startX)
      const dy = Math.abs(tt.clientY - dragRef.current.startY)
      if (dx > 4 || dy > 4) {
        dragRef.current.moved = true
        const rect = fabRef.current?.getBoundingClientRect()
        const rw = rect?.width || 120
        const rh = rect?.height || 56
        const newRight  = Math.max(8, Math.min(dragRef.current.screenW - rw - 8, dragRef.current.origRight - (tt.clientX - dragRef.current.startX)))
        const newBottom = Math.max(getMinBottom(), Math.min(dragRef.current.screenH - rh - 8, dragRef.current.origBottom - (tt.clientY - dragRef.current.startY)))
        setPos({ right: newRight, bottom: newBottom })
      }
    }

    const onEnd = () => {
      dragRef.current.dragging = false
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
    }

    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onEnd)
  }, [pos])

  // Click only fires if not dragged
  const handlePillClick = useCallback(() => {
    if (dragRef.current.moved) return
    setOpen(o => !o)
  }, [])

  const fetchQueryCount = async () => {
    if (!seller?.id) return
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
    const { count }  = await supabase.from('tara_queries').select('*', { count:'exact', head:true }).eq('seller_id', seller.id).gte('created_at', monthStart)
    setQueriesUsed(count || 0)
  }

  const fetchBusinessContext = async () => {
    if (!seller?.id) return ''
    const now            = new Date()
    const monthStart     = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth()-1, 1).toISOString()
    const lastMonthEnd   = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59).toISOString()
    const weekStart      = new Date(Date.now()-7*86400000).toISOString()
    const yearStart      = new Date(now.getFullYear(), 0, 1).toISOString()

    const [ordersRes,productsRes,customersRes,variantsRes,monthRes,lastMonthRes,weekRes,yearRes,invoicesRes,eventsRes,goalsRes,boostsRes] = await Promise.all([
      supabase.from('orders').select('id,order_code,status,total_amount,product_name,product_code,variant_name,quantity,unit_price,discount_amount,delivery_charge,payment_status,payment_method,order_source,branch_name,notes,created_at,customers(name,phone,district,division)').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(100),
      supabase.from('products').select('id,name,stock,sell_price,buy_price,product_code,category,product_type,color,season,status').eq('seller_id',seller.id).order('name').limit(200),
      supabase.from('customers').select('id,name,phone,district,division,facebook_url,created_at').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(100),
      supabase.from('product_variants').select('id,product_id,variant_name,stock,sell_price').eq('seller_id',seller.id).limit(200),
      supabase.from('orders').select('total_amount,status,product_name,payment_status').eq('seller_id',seller.id).gte('created_at',monthStart),
      supabase.from('orders').select('total_amount,status,product_name').eq('seller_id',seller.id).gte('created_at',lastMonthStart).lte('created_at',lastMonthEnd),
      supabase.from('orders').select('total_amount,status').eq('seller_id',seller.id).gte('created_at',weekStart),
      supabase.from('orders').select('total_amount,status').eq('seller_id',seller.id).gte('created_at',yearStart),
      supabase.from('seller_invoices').select('id,invoice_number,total_amount,status,client_name,created_at').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(20),
      supabase.from('seller_events').select('id,name,start_date,end_date,location,status,stall_fee,transport_cost').eq('seller_id',seller.id).order('start_date',{ascending:false}).limit(10),
      supabase.from('seller_goals').select('id,month,year,revenue_goal,order_goal,notes').eq('seller_id',seller.id).order('year',{ascending:false}).limit(6),
      supabase.from('boosts').select('id,fb_campaign_name,status,budget_bdt,goal,launched_at,ends_at,result_reach,result_clicks').eq('seller_id',seller.id).order('created_at',{ascending:false}).limit(10),
    ])

    const orders      = ordersRes.data      || []
    const products    = productsRes.data    || []
    const customers   = customersRes.data   || []
    const variants    = variantsRes.data    || []
    const monthOrds   = monthRes.data       || []
    const lastMonOrds = lastMonthRes.data   || []
    const weekOrds    = weekRes.data        || []
    const yearOrds    = yearRes.data        || []
    const invoices    = invoicesRes.data    || []
    const events      = eventsRes.data      || []
    const goals       = goalsRes.data       || []
    const boosts      = boostsRes.data      || []

    const monthRev   = monthOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
    const lastMonRev = lastMonOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
    const weekRev    = weekOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)
    const yearRev    = yearOrds.reduce((s,o)=>s+Number(o.total_amount||0),0)

    const statusCount = {}
    orders.forEach(o => { statusCount[o.status]=(statusCount[o.status]||0)+1 })
    const pending   = (statusCount.pending||0)+(statusCount.received||0)
    const confirmed = statusCount.confirmed||0
    const shipped   = statusCount.shipped||0
    const closed    = statusCount.closed||0
    const returned  = statusCount.return||0

    const paidCount   = monthOrds.filter(o=>o.payment_status==='Paid in full').length
    const unpaidCount = monthOrds.filter(o=>o.payment_status==='Unpaid COD').length

    const prodCount = {}
    monthOrds.forEach(o => { if(o.product_name) prodCount[o.product_name]=(prodCount[o.product_name]||0)+1 })
    const topMonthProds = Object.entries(prodCount).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([n,c])=>`${n}(${c})`)

    const lastProdCount = {}
    lastMonOrds.forEach(o => { if(o.product_name) lastProdCount[o.product_name]=(lastProdCount[o.product_name]||0)+1 })
    const topLastProds = Object.entries(lastProdCount).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([n,c])=>`${n}(${c})`)

    const outOfStock = products.filter(p=>p.stock===0).map(p=>p.name)
    const lowStock   = products.filter(p=>p.stock>0&&p.stock<=5).map(p=>`${p.name}(${p.stock})`)

    const custOrders = {}
    orders.forEach(o => { if(o.customers?.name) custOrders[o.customers.name]=(custOrders[o.customers.name]||0)+1 })
    const topCustomers = Object.entries(custOrders).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([n,c])=>`${n}(${c} orders)`)

    const monthName   = now.toLocaleDateString('en',{month:'long'})
    const lastMonName = new Date(now.getFullYear(),now.getMonth()-1,1).toLocaleDateString('en',{month:'long'})
    const currentGoal = goals.find(g=>g.month===now.getMonth()+1&&g.year===now.getFullYear())
    const activeBoosts = boosts.filter(b=>b.status==='running')

    const sellerCountry = seller?.country||'BD'
    const isAU = sellerCountry==='AU'||sellerCountry==='Australia'
    const isUS = sellerCountry==='US'||sellerCountry==='USA'
    const isBD = !isAU&&!isUS

    const culturalContext = isBD ? `
BANGLADESH F-COMMERCE CONTEXT:
Language awareness: Always follow the seller's actual language — if they write in English respond in English, if Bengali respond in Bengali, if mixed respond mixed. Never assume Bengali just because seller is from BD.
Bengali tone: Use "তুমি/তোমার" (not "আপনি/আপনার") — friendly and warm, like a trusted friend.
Peak occasions: Eid ul Fitr (biggest — kameez/saree/panjabi), Eid ul Adha (2nd biggest), Pahela Baishakh Apr 14, Pahela Falgun, Durga Puja, Winter collection Nov-Feb, Wedding season Oct-Jan.
Islamic calendar dates (Eid, Ramadan): always say "approximately" or "expected around" — exact dates depend on moon sighting and may vary by 1-2 days. If today's date shows the occasion has already passed this year, say so clearly and give next year's approximate date instead.
F-commerce: Facebook Live selling dominant, COD 60-70% normal, bKash advance = trust signal.
Payment MFS: bKash, Nagad, Rocket, Upay. Couriers: Pathao (Dhaka ৳60-80), Steadfast/RedX (outside ৳100-130).`
    : isAU ? `AUSTRALIA: Seasons opposite. GST 10%. PayID, Afterpay. Peak: Christmas, Boxing Day, EOFY.`
    : `USA: Credit card, PayPal. Peak: Black Friday, Cyber Monday, Christmas.`

    return `=== SELLER PROFILE ===
Shop: ${seller.shop_name||'Unknown'} | Plan: ${plan}
Currency: ${seller.seller_currency||'BDT'} | Country: ${seller.country||'BD'}

=== REVENUE ===
${monthName} (this month): ৳${Math.round(monthRev).toLocaleString()} from ${monthOrds.length} orders
${lastMonName} (last month): ৳${Math.round(lastMonRev).toLocaleString()} from ${lastMonOrds.length} orders
This week: ৳${Math.round(weekRev).toLocaleString()} | This year: ৳${Math.round(yearRev).toLocaleString()}
Month-on-month: ${lastMonRev>0?((monthRev-lastMonRev)/lastMonRev*100).toFixed(1)+'%':'N/A'}

=== ORDERS ===
Total: ${orders.length} | ${monthName}: ${monthOrds.length} | Last month: ${lastMonOrds.length}
Status: Pending/Received=${pending}, Confirmed=${confirmed}, Shipped=${shipped}, Closed=${closed}, Returned=${returned}
Payment: Paid=${paidCount}, Unpaid COD=${unpaidCount}
Top products ${monthName}: ${topMonthProds.join(', ')||'No data'}
Top products ${lastMonName}: ${topLastProds.join(', ')||'No data'}

=== RECENT 15 ORDERS ===
${orders.slice(0,15).map(o=>`${o.order_code}|${o.customers?.name||'Unknown'}|${o.product_name||'N/A'}|${o.status}|৳${o.total_amount}|${o.payment_status}`).join('\n')}

=== PRODUCTS (${products.length} total) ===
Out of stock(${outOfStock.length}): ${outOfStock.slice(0,10).join(', ')||'None'}
Low stock ≤5(${lowStock.length}): ${lowStock.slice(0,10).join(', ')||'None'}
Products: ${products.slice(0,30).map(p=>`${p.name}|stock:${p.stock}|price:৳${p.sell_price}|${p.category||'N/A'}`).join(' / ')}
Variants: ${variants.slice(0,20).map(v=>`${v.variant_name}|stock:${v.stock}`).join(', ')}

=== CUSTOMERS (${customers.length} total) ===
Top buyers: ${topCustomers.join(', ')||'No data'}
Recent: ${customers.slice(0,10).map(c=>`${c.name}|${c.phone}|${c.district||'N/A'}`).join(' / ')}

=== INVOICES ===
${invoices.slice(0,5).map(i=>`${i.invoice_number}|${i.client_name}|৳${i.total_amount}|${i.status}`).join('\n')||'No invoices'}

=== EVENTS ===
${events.slice(0,3).map(e=>`${e.name}|${e.start_date}|${e.location||'N/A'}|${e.status}`).join('\n')||'No events'}

=== GOALS ===
${currentGoal?`${monthName}: Revenue=৳${currentGoal.revenue_goal?.toLocaleString()}, Orders=${currentGoal.order_goal}, Progress: Revenue ${((monthRev/currentGoal.revenue_goal)*100).toFixed(0)}%, Orders ${((monthOrds.length/currentGoal.order_goal)*100).toFixed(0)}%`:'No goal set'}

=== ADBOOST ===
Active: ${activeBoosts.length} | Total: ${boosts.length}
${activeBoosts.map(b=>`${b.fb_campaign_name}|${b.goal}|ends:${b.ends_at?.split('T')[0]}`).join(', ')||'None'}

${culturalContext}`
  }

  const ask = async (question) => {
    if (!question.trim()||loading||!canAsk) return
    setMessages(prev=>[...prev,{role:'user',text:question}])
    setInput('')
    setLoading(true)

    try {
      const ctx = await fetchBusinessContext()
      const recentHistory = messages.slice(-8)

      const systemPrompt = `You are Tara, the AI Business Agent for ${seller?.shop_name||'this shop'} on PageConnect.
Today: ${new Date().toLocaleDateString('en',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}

PERSONALITY: Warm, smart, encouraging. Like a brilliant friend who knows your business inside out. Natural conversational tone, light emojis when appropriate.

LANGUAGE RULE: ALWAYS respond in the exact same language the seller used. English = English. Bengali = Bengali. Mixed = mixed. Never switch.

SELLER BUSINESS DATA:
${ctx}

ANSWER RULES:
- Use exact numbers from business data above
- General world/business questions: answer confidently
- Sensitive topics (politics, religion controversies, race): neutral, no sides
- Islamic calendar dates (Eid, Ramadan): always say "approximately" or "expected around" — exact dates depend on moon sighting. If already passed this year, give next year's approximate date.
- Legal/tax: general guidance only
- Never support illegal activities

CONVERSATION HISTORY:
${recentHistory.map(m=>`${m.role==='user'?'Seller':'Tara'}: ${m.text}`).join('\n')}

Seller's question: ${question}

(Responses are mostly accurate but important decisions should be verified.)`

      const response = await fetch(`${GEMINI_URL}?key=${process.env.REACT_APP_GEMINI_KEY}`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          contents:[{parts:[{text:systemPrompt}]}],
          generationConfig:{temperature:0.8},
        })
      })

      if(!response.ok){const err=await response.json();throw new Error(err?.error?.message||'API error')}
      const data   = await response.json()
      const answer = data?.candidates?.[0]?.content?.parts?.[0]?.text
      if(!answer) throw new Error('Empty response')

      setMessages(prev=>[...prev,{role:'assistant',text:answer.trim()}])
      await supabase.from('tara_queries').insert({seller_id:seller.id,question:question.slice(0,500)})
      setQueriesUsed(prev=>prev+1)

    } catch(err) {
      setMessages(prev=>[...prev,{
        role:'assistant',
        text:lang==='bn'?'আরে! কিছু একটা গোলমাল হয়ে গেছে 😅 একটু পরে আবার চেষ্টা করো!':'Oops! Something went wrong 😅 Try again in a moment!',
      }])
    }
    setLoading(false)
  }

  const handleKey = (e) => {
    if (e.key==='Enter'&&!e.shiftKey) { e.preventDefault(); ask(input) }
  }

  if (limit===0) return null

  const suggestions = lang==='bn' ? SUGGESTIONS.bn : SUGGESTIONS.en

  return (
    <>
      <style>{`
        @keyframes taraSlideUp {
          from { opacity:0; transform:translateY(12px) scale(0.97) }
          to   { opacity:1; transform:translateY(0) scale(1) }
        }
        @keyframes taraTyping {
          0%,60%,100% { transform:translateY(0); opacity:0.4 }
          30% { transform:translateY(-5px); opacity:1 }
        }
        @keyframes taraPulse {
          0%,100% { box-shadow:0 4px 20px rgba(11,31,58,0.35), 0 0 0 0 rgba(45,212,176,0.4) }
          50%      { box-shadow:0 4px 24px rgba(11,31,58,0.45), 0 0 0 8px rgba(45,212,176,0) }
        }
        @keyframes spin { to { transform:rotate(360deg) } }
        .tara-msg { white-space:pre-wrap; line-height:1.6 }
        .tara-chip:hover { background:var(--primary-light)!important; border-color:var(--primary-border)!important; color:var(--primary)!important }
      `}</style>

      {/* ── Chat panel ── */}
      {open && (
        <div ref={panelRef}
          style={{ position:'fixed', bottom:pos.bottom+72, right:pos.right, width:'min(380px,calc(100vw - 32px))', height:'min(560px,calc(100vh - 120px))', borderRadius:'20px', background:'var(--bg-card)', border:'1px solid var(--border)', boxShadow:'0 20px 60px rgba(0,0,0,0.15)', zIndex:999, display:'flex', flexDirection:'column', overflow:'hidden', animation:'taraSlideUp 0.2s ease forwards', fontFamily:'var(--font-body)' }}>

          {/* Header */}
          <div style={{ padding:'14px 16px', background:'linear-gradient(135deg,#0B1F3A 0%,#1a3a6e 100%)', display:'flex', alignItems:'center', gap:'10px', flexShrink:0 }}>
            <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'rgba(255,255,255,0.1)', border:'1.5px solid rgba(45,212,176,0.4)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <TaraStarIcon size={18} color='#2DD4B0'/>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
                <span style={{ fontSize:'14px', fontWeight:700, color:'#FFF', fontFamily:'var(--font-ui)' }}>Tara</span>
                <span style={{ fontSize:'9px', fontWeight:700, background:'rgba(45,212,176,0.2)', color:'#2DD4B0', padding:'2px 6px', borderRadius:'5px', letterSpacing:'0.06em', border:'1px solid rgba(45,212,176,0.3)' }}>AI AGENT</span>
              </div>
              <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)', marginTop:'1px' }}>
                {remaining} of {limit} queries left this month
              </div>
            </div>
            <button onClick={()=>setOpen(false)}
              style={{ width:'28px', height:'28px', borderRadius:'8px', background:'rgba(255,255,255,0.1)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.7)', fontSize:'16px' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.2)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}>
              ×
            </button>
          </div>

          {/* Usage bar */}
          <div style={{ height:'2px', background:'rgba(255,255,255,0.06)', flexShrink:0 }}>
            <div style={{ height:'100%', width:`${Math.min(100,(queriesUsed/limit)*100)}%`, background:'linear-gradient(90deg,#2DD4B0,#0EA5E9)', transition:'width 0.4s' }}/>
          </div>

          {/* Messages */}
          <div style={{ flex:1, overflowY:'auto', padding:'14px 14px 8px', display:'flex', flexDirection:'column', gap:'12px', scrollbarWidth:'thin', scrollbarColor:'var(--border) transparent' }}>
            {messages.map((msg,i)=>{
              const isUser    = msg.role==='user'
              const showAvatar = !isUser&&(i===0||messages[i-1]?.role==='user')
              return (
                <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:isUser?'flex-end':'flex-start', gap:'3px' }}>
                  <div style={{ display:'flex', alignItems:'flex-end', gap:'7px', flexDirection:isUser?'row-reverse':'row' }}>
                    {!isUser && (
                      <div style={{ width:'28px', height:'28px', borderRadius:'50%', background:showAvatar?'linear-gradient(135deg,#0B1F3A,#1a3a6e)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {showAvatar&&<TaraStarIcon size={13} color='#2DD4B0'/>}
                      </div>
                    )}
                    <div style={{ maxWidth:'80%', padding:'10px 14px', borderRadius:isUser?'18px 18px 4px 18px':'18px 18px 18px 4px', background:isUser?'linear-gradient(135deg,#0B1F3A,#1a3a6e)':'var(--bg-card)', color:isUser?'#FFF':'var(--text-primary)', fontSize:'13px', border:isUser?'none':'1px solid var(--border)', boxShadow:isUser?'0 2px 8px rgba(11,31,58,0.25)':'var(--shadow-sm)' }}>
                      <span className="tara-msg">{msg.text}</span>
                    </div>
                  </div>
                </div>
              )
            })}

            {loading && (
              <div style={{ display:'flex', alignItems:'flex-end', gap:'7px' }}>
                <div style={{ width:'28px', height:'28px', borderRadius:'50%', background:'linear-gradient(135deg,#0B1F3A,#1a3a6e)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <TaraStarIcon size={13} color='#2DD4B0'/>
                </div>
                <TypingIndicator/>
              </div>
            )}

            {/* Suggestion chips — only at start */}
            {messages.length<=1&&!loading&&(
              <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginTop:'4px', paddingLeft:'35px' }}>
                <div style={{ fontSize:'10.5px', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:'var(--font-ui)', marginBottom:'2px' }}>Try asking</div>
                {suggestions.map((s,i)=>(
                  <button key={i} onClick={()=>ask(s)} className="tara-chip"
                    style={{ padding:'8px 12px', borderRadius:'var(--radius-md)', background:'var(--bg-muted)', border:'1px solid var(--border)', fontSize:'12.5px', color:'var(--text-primary)', cursor:'pointer', textAlign:'left', fontFamily:'var(--font-body)', lineHeight:1.4, transition:'all 0.12s' }}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef}/>
          </div>

          {/* Limit reached */}
          {!canAsk&&limit>0&&(
            <div style={{ padding:'10px 14px', background:'var(--error-light)', borderTop:'1px solid var(--error-border)', fontSize:'12px', color:'var(--error)', textAlign:'center', flexShrink:0 }}>
              {lang==='bn'?'এই মাসের limit শেষ। পরের মাসের ১ তারিখে reset হবে 🔄':"Monthly query limit reached. Resets on the 1st! 🔄"}
            </div>
          )}

          {/* Input */}
          {canAsk&&(
            <div style={{ padding:'10px 12px', borderTop:'1px solid var(--border)', background:'var(--bg-muted)', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'flex-end', gap:'8px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'8px 8px 8px 14px' }}
                onFocusCapture={e=>{ e.currentTarget.style.borderColor='var(--border-focus)'; e.currentTarget.style.boxShadow='0 0 0 3px var(--primary-light)' }}
                onBlurCapture={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.boxShadow='none' }}>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e=>{ if(e.target.value.length<=MAX_CHARS) setInput(e.target.value) }}
                  onKeyDown={handleKey}
                  placeholder={lang==='bn'?'Tara কে জিজ্ঞেস করো...':'Ask Tara anything...'}
                  rows={1}
                  disabled={loading}
                  style={{ flex:1, border:'none', outline:'none', fontSize:'13px', color:'var(--text-primary)', background:'transparent', fontFamily:'var(--font-body)', resize:'none', maxHeight:'80px', overflowY:'auto', lineHeight:1.5, padding:0 }}
                />
                <button onClick={()=>ask(input)} disabled={!input.trim()||loading}
                  style={{ width:'34px', height:'34px', borderRadius:'10px', border:'none', background:input.trim()&&!loading?'linear-gradient(135deg,#0B1F3A,#1a3a6e)':'var(--bg-muted)', cursor:input.trim()&&!loading?'pointer':'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'var(--transition-base)' }}>
                  {loading
                    ?<div style={{ width:'14px', height:'14px', borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#2DD4B0', animation:'spin 0.6s linear infinite' }}/>
                    :<svg width="15" height="15" viewBox="0 0 20 20" fill={input.trim()?'white':'var(--text-muted)'}><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                  }
                </button>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:'5px', padding:'0 2px' }}>
                <span style={{ fontSize:'10.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>Enter to send · Shift+Enter for new line</span>
                <span style={{ fontSize:'10.5px', color:charsLeft<50?'var(--error)':'var(--text-muted)', fontFamily:'var(--font-body)' }}>{charsLeft} left</span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{ padding:'4px 14px 8px', background:'var(--bg-muted)', textAlign:'center', flexShrink:0 }}>
            <span style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>Tara by PageConnect · Powered by Gemini</span>
          </div>
        </div>
      )}

      {/* ── Pill trigger — "Ask Tara AI" label like before ── */}
      <div
        ref={fabRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        style={{ position:'fixed', bottom:pos.bottom, right:pos.right, zIndex:1000, userSelect:'none', touchAction:'none', cursor:'grab' }}>
        <button
          onClick={handlePillClick}
          style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 18px 10px 12px', borderRadius:'28px', background:open?'linear-gradient(135deg,#1a3a6e,#0B1F3A)':'linear-gradient(135deg,#0B1F3A,#1a3a6e)', border:'2px solid rgba(45,212,176,0.3)', cursor:'pointer', boxShadow:'0 4px 20px rgba(11,31,58,0.35)', fontFamily:'var(--font-ui)', transition:'all 0.2s cubic-bezier(0.34,1.56,0.64,1)', animation:!open?'taraPulse 2.5s ease-in-out infinite':'none' }}
          onMouseEnter={e=>{ e.currentTarget.style.boxShadow='0 8px 28px rgba(11,31,58,0.45)'; e.currentTarget.style.transform='translateY(-1px)' }}
          onMouseLeave={e=>{ e.currentTarget.style.boxShadow='0 4px 20px rgba(11,31,58,0.35)'; e.currentTarget.style.transform='translateY(0)' }}>
          {/* Icon circle */}
          <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:open?'rgba(255,255,255,0.15)':'rgba(45,212,176,0.15)', border:'1px solid rgba(45,212,176,0.3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {open
              ?<svg width="14" height="14" viewBox="0 0 20 20" fill="rgba(255,255,255,0.8)"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
              :<TaraStarIcon size={16} color='#2DD4B0'/>
            }
          </div>
          {/* Label */}
          <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
            <span style={{ fontSize:'13px', fontWeight:700, color:'#FFF', lineHeight:1 }}>
              {open?'Close':'Ask Tara'}
            </span>
            {!open&&<span style={{ fontSize:'9px', fontWeight:700, background:'rgba(45,212,176,0.2)', color:'#2DD4B0', padding:'2px 6px', borderRadius:'4px', letterSpacing:'0.04em', border:'1px solid rgba(45,212,176,0.3)' }}>AI</span>}
          </div>
        </button>
      </div>
    </>
  )
}